'use client';
import React, { useContext } from 'react';
import { KeyEditorContext } from './KeyEditorContext';
import KeyEditorProvider from './KeyEditorProvider';

import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import {
  TCompareDefinition,
  createCompare,
  updateCompare,
  ICompareResponse,
  ISourceDataFrame,
} from '@/lib/tabularCompareApi';

import { Stack, Card } from '@/ui/primatives';
import Button from '@/ui/Button';

import KeyPicker from './KeyPicker';
import * as Styled from './styled';

import { IRepository, IResourceVersion } from '@/types';

const KeyEditorBody: React.FC<{ onSubmit?: (definition: TCompareDefinition) => void }> = ({ onSubmit }) => {
  const { associations, isValid, errorMessages, compiledDefinition, isDirty, reset, canEdit } =
    useContext(KeyEditorContext);

  const updatedDefinition: TCompareDefinition = { ...compiledDefinition };

  return (
    <Stack gap={20}>
      {associations.length > 0 ? <KeyPicker /> : <Card.Info stretch={false}>There are no associated fields.</Card.Info>}
      {!isValid && errorMessages.length > 0 && (
        <Card.Error>
          <Stack gap={5}>
            {errorMessages.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </Stack>
        </Card.Error>
      )}
      {isDirty && canEdit && (
        <Styled.Actions>
          <Button.Basic small disabled={!isValid} onClick={() => onSubmit?.(updatedDefinition)}>
            Update
          </Button.Basic>
          <Button.Secondary className="danger" small onClick={reset}>
            Cancel
          </Button.Secondary>
        </Styled.Actions>
      )}
    </Stack>
  );
};

const getDataFrameResourceVersion = (sourceDataFrame: ISourceDataFrame): IResourceVersion => {
  return {
    path: sourceDataFrame.path,
    version: sourceDataFrame.version,
  };
};

const KeyEditor: React.FC<{
  repository: IRepository;
  compareResponse: ICompareResponse;
  children?: React.ReactNode;
}> = ({ repository, compareResponse, children }) => {
  const { compare } = compareResponse;
  // const repository = compareResponse.repository;
  const definition = compare?.definition;
  const compareId = compare?.compare_number.toString();
  const schemaLeft = compareResponse?.source_data_frames.left?.schema;
  const schemaRight = compareResponse?.source_data_frames.right?.schema;

  // console.log('compareResponse', compareResponse);

  const iCompare: TCompareDefinition = {
    ...definition,
    data_frames: {
      left: getDataFrameResourceVersion(compareResponse.source_data_frames.left),
      right: getDataFrameResourceVersion(compareResponse.source_data_frames.right),
    },
  };

  const commitCompare = async (definition: TCompareDefinition) => {
    const { compare: updatedCompare } = compareId
      ? await updateCompare({
          compareId,
          namespace: repository.namespace,
          repositoryName: repository.name,
          payload: definition,
        })
      : await createCompare({ namespace: repository.namespace, repositoryName: repository.name, payload: definition });
    // window.location.href = `/${repository.namespace}/${repository.name}/compare/${compare.compare_number}`;
    window.location.reload();
  };

  return (
    <Styled.KeyEditor>
      <KeyEditorProvider
        repository={repository}
        compare={iCompare}
        schemaLeft={schemaLeft as IDataFrameSchema}
        schemaRight={schemaRight as IDataFrameSchema}
        requireFieldNameMatch={false}
      >
        {children}
        <KeyEditorBody onSubmit={(definition) => commitCompare(definition)} />
      </KeyEditorProvider>
    </Styled.KeyEditor>
  );
};

export default KeyEditor;
